import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 「改动后自动写入设备」的通用逻辑，模式来自灯光页：
 *
 *   - 只有用户动过的改动才排写入（markDirty），设备回读引起的表单
 *     setState 不会再触发写入；
 *   - 拖滑条会连续产生改动，写入必须等停手（防抖），否则每一格都要
 *     走一次多包写入 + 确认包；
 *   - 用户动过界面之后表单以界面为准，页面应据 ownedByUser() 停止用
 *     设备回读回灌表单——回读比写入慢，拿它覆盖表单会把界面打回旧值；
 *   - 断开连接后界面上的值不再代表设备状态，待写状态全部清空；
 *   - 离开页面时把还在防抖窗口里的改动补写一次，避免改完就切页丢设置。
 *
 * 写入本身串行化：上一次写入还没结束时，下一次到点的写入排在其后，
 * 不会与之并发抢 HID 通道。
 */
export const AUTO_APPLY_DELAY_MS = 350;

export const AUTO_APPLY_STATUS_TEXT = Object.freeze({
  idle: '已与设备同步',
  saving: '正在保存…',
  saved: '已保存到设备',
  error: '保存失败',
});

/** 头部状态行：未连接与读取中优先于保存状态 */
export const resolveAutoApplyStatus = ({ isConnected, ready, saveStatus }) => {
  if (!isConnected) return { kind: 'error', text: '设备未连接' };
  if (!ready) return { kind: 'saving', text: '正在读取设备参数…' };
  return { kind: saveStatus, text: AUTO_APPLY_STATUS_TEXT[saveStatus] };
};

/**
 * @param {Object} options
 * @param {*} options.value - 被监听的表单状态；改动且 markDirty 后触发写入
 * @param {Function} options.apply - async (value) => void，把快照写入设备
 * @param {boolean} options.enabled - 允许写入（通常 isConnected && ready）
 * @param {boolean} options.isConnected - 断开时清空待写状态
 * @param {Function} [options.onError] - 写入失败回调（页面用来弹 toast）
 * @param {number} [options.delay]
 * @returns {{ saveStatus, markDirty, ownedByUser, release }}
 */
export function useAutoApply({
  value,
  apply,
  enabled,
  isConnected,
  onError,
  delay = AUTO_APPLY_DELAY_MS,
}) {
  const [saveStatus, setSaveStatus] = useState('idle');
  // 用户动过且还没写入设备
  const pendingRef = useRef(false);
  // 用户在本次连接里动过界面：此后表单以界面为准，设备回读不再回灌
  const ownedRef = useRef(false);
  // 串行化队列：下一次写入永远排在上一次完成之后
  const chainRef = useRef(Promise.resolve());
  // 最新的 apply / onError / 表单值，供定时器与卸载补写读取
  const latestRef = useRef({ apply, onError, value, enabled });

  useEffect(() => {
    latestRef.current = { apply, onError, value, enabled };
  });

  /** 用户改动了表单，排一次自动写入。设备回读引起的 setState 不要调它。 */
  const markDirty = useCallback(() => {
    pendingRef.current = true;
    ownedRef.current = true;
  }, []);

  /** 表单是否已由用户接管（接管后设备回读不应回灌表单） */
  const ownedByUser = useCallback(() => ownedRef.current, []);

  /**
   * 把表单交还给设备回读（例如恢复出厂后重新读取全部数据时），
   * 同时丢弃还没写入的改动。
   */
  const release = useCallback(() => {
    pendingRef.current = false;
    ownedRef.current = false;
  }, []);

  // 断开后界面上的值不再代表设备状态：重连后重新以设备回读为准
  useEffect(() => {
    if (isConnected) return;
    pendingRef.current = false;
    ownedRef.current = false;
    setSaveStatus('idle');
  }, [isConnected]);

  const enqueue = useCallback((snapshot, { silent = false } = {}) => {
    const run = async () => {
      if (!silent) setSaveStatus('saving');
      try {
        await latestRef.current.apply(snapshot);
        if (!silent) setSaveStatus('saved');
      } catch (error) {
        console.error('自动应用失败:', error);
        if (!silent) {
          setSaveStatus('error');
          latestRef.current.onError?.(error);
        }
      }
    };
    // 上一次写入无论成败，下一次都继续排队
    chainRef.current = chainRef.current.then(run, run);
    return chainRef.current;
  }, []);

  // 用户停手后自动写入设备。写入前先清标记，设备回读触发的 setState
  // 不会再排一次写入。
  useEffect(() => {
    if (!pendingRef.current) return undefined;
    if (!enabled) return undefined;

    const snapshot = value;
    const timer = setTimeout(() => {
      pendingRef.current = false;
      enqueue(snapshot);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, enabled, delay, enqueue]);

  // 离开页面时把还在防抖窗口里的改动补写一次。组件已卸载，
  // 不再更新状态（silent），失败只记日志。
  useEffect(
    () => () => {
      if (!pendingRef.current) return;
      const latest = latestRef.current;
      if (!latest.enabled) return;
      pendingRef.current = false;
      enqueue(latest.value, { silent: true });
    },
    [enqueue]
  );

  return { saveStatus, markDirty, ownedByUser, release };
}
